import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, TextInput } from "./kit";

export default function ServicesManager() {
  const [details, setDetails] = useState(null);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/content"), api.get("/admin/client-countries")])
      .then(([c, cc]) => {
        setDetails(c.data.service_details && c.data.service_details.services ? c.data.service_details : { services: [] });
        setCountries(cc.data || []);
      })
      .catch(() => toast.error("Failed to load services"));
  }, []);

  if (!details) return null;

  const setCount = (i, v) =>
    setDetails({ ...details, services: details.services.map((s, idx) => (idx !== i ? s : { ...s, available_specialists: parseInt(v, 10) || 0 })) });

  const toggleCountry = (i, name) =>
    setDetails({
      ...details,
      services: details.services.map((s, idx) => {
        if (idx !== i) return s;
        const has = (s.supported_countries || []).includes(name);
        return { ...s, supported_countries: has ? s.supported_countries.filter((n) => n !== name) : [...(s.supported_countries || []), name] };
      }),
    });

  const save = async (i) => {
    try {
      await api.put("/admin/content/service_details", { data: details });
      toast.success("Service details updated — live on the site now");
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-services-manager">
      <Card title="Services" desc="Edit the available specialist count and the countries list for each service card. Changes reflect live in the service modal popups on the website.">
        {details.services.map((s, i) => (
          <div key={s.title} className="rounded-xl border border-white/10 bg-[#0A0F2C]/40 p-4 mb-4" data-testid={`services-row-${i}`}>
            <div className="flex items-center justify-between gap-4">
              <h4 className="font-semibold text-white text-sm">{s.title}</h4>
              <button className="btn btn-sm btn-primary" onClick={() => save(i)} data-testid={`services-save-${i}`}>Save</button>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <label className="text-xs text-slate-400 whitespace-nowrap">Available specialists</label>
              <TextInput type="number" min="0" className="w-24" value={s.available_specialists ?? 0} onChange={(e) => setCount(i, e.target.value)} data-testid={`services-specialists-${i}`} />
            </div>
            <div className="mt-4">
              <div className="text-xs text-slate-400 mb-2">Countries supported (shown with flags in the modal)</div>
              <div className="flex flex-wrap gap-2">
                {countries.map((c) => (
                  <label key={c.id} className="flex items-center gap-1.5 text-xs text-slate-200 cursor-pointer border border-white/10 rounded-full px-3 py-1.5 hover:border-cyan-400/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={(s.supported_countries || []).includes(c.name)}
                      onChange={() => toggleCountry(i, c.name)}
                      className="accent-cyan-400"
                      data-testid={`services-country-${i}-${c.name.replace(/ /g, "_")}`}
                    />
                    {c.name}
                  </label>
                ))}
                {countries.length === 0 && <span className="text-xs text-slate-500">Add countries in Client Countries first.</span>}
              </div>
            </div>
          </div>
        ))}
        {details.services.length === 0 && <div className="py-6 text-center text-slate-500 text-sm">No services found.</div>}
      </Card>
    </div>
  );
}
